import React, { useMemo } from "react";
import { createAvatar } from "@dicebear/core";
import { notionistsNeutral, initials } from "@dicebear/collection";

// Size presets for easy usage
export const AVATAR_SIZES = {
  xxs: 16,
  xs: 22,
  sm: 28,
  md: 36,
  lg: 44,
  xl: 48,
} as const;

export type AvatarSizePreset = keyof typeof AVATAR_SIZES;

export type AvatarStyle = "default" | "initials";

export interface AvatarProps {
  /** Seed value for generating consistent avatars (e.g. user id) */
  seed?: string;
  /** Size of the avatar - either a preset ('xs', 'sm', 'md', 'lg', 'xl') or exact pixel value */
  size?: AvatarSizePreset | number;
  /** Optional CSS class name */
  className?: string;
  /** Optional inline styles */
  style?: React.CSSProperties;
  /** Avatar style to use - 'default' uses notionistsNeutral, 'initials' uses initials style */
  avatarStyle?: AvatarStyle;
  /** Background color for initials style - if not provided, will use from AVATAR_COLORS */
  backgroundColor?: string[];
  /** Optional click handler */
  onClick?: () => void;
  /** Optional image URL to display instead of a generated avatar */
  image?: string;
}

// High-contrast vibrant background colors for avatars (shuffled)
const AVATAR_COLORS = [
  "FF9A76", // Peach (was Bright coral)
  "00A8FF", // Vivid blue
  "FFD000", // Vibrant gold
  "9575CD", // Medium purple
  "23CD98", // Mint
  "FF8BA7", // Salmon pink
  "536DFE", // Indigo
  "64DD17", // Bright green
  "CE93D8", // Light orchid
  "FFCC00", // Marigold
  "8E87FA", // Periwinkle blue (was Vibrant coral)
  "BA68C8", // Medium orchid
  "00BCD4", // Cyan
  "B2FF59", // Lime
  "FF6A8B", // Flamingo
  "4DB6AC", // Light teal
  "D500F9", // Electric purple
  "FFD740", // Amber
  "00C853", // Vibrant green
  "A586E5", // Medium lilac
  "FF9CC0", // Light pink
  "43A047", // Green
  "49BCF6", // Sky blue
  "FA7AA5", // Pink coral
  "00897B", // Teal green
  "FDE74C", // Sunshine yellow
  "26B6C7", // Medium teal
  "76FF03", // Light green
  "B39DDB", // Light purple
  "5EBBDC", // Steel blue (was Deep orange)
  "00E5E0", // Bright aqua
  "8C9EFF", // Light indigo
  "F9DB63", // Mellow gold
  "99D6EA", // Sky blue (was Light coral)
  "42DCA3", // Spring green
  "EA80FC", // Bright lavender
  "26C6DA", // Bright cyan
  "FFDF4D", // Mustard
  "7CB342", // Light green
  "4FC3F7", // Light blue
  "00ACC1", // Medium cyan
  "FF7BAC", // Bubblegum
  "C0CA33", // Lime
  "5AEBC2", // Light jade
  "86BBFF", // Periwinkle blue
  "F48FB1", // Baby pink
  "FDD835", // Yellow
  "FFAB00", // Amber
  "FFE57F", // Light gold
];

export const Avatar: React.FC<AvatarProps> = ({
  seed,
  size = "md",
  className = "",
  style = {},
  avatarStyle = "default",
  backgroundColor,
  onClick,
  image,
}) => {
  // Use a default seed if none is provided
  const finalSeed = seed || "default";

  // Calculate the final size in pixels based on whether size is a preset or exact value
  const finalSize = typeof size === "string" ? AVATAR_SIZES[size] : size;

  const avatarSvg = useMemo(() => {
    if (image) {
      return image;
    }
    if (avatarStyle === "initials") {
      return createAvatar(initials, {
        seed: finalSeed,
        size: finalSize,
        scale: 80,
        radius: 42,
        fontWeight: 600,
        fontFamily: ["Helvetica"],
        backgroundColor: AVATAR_COLORS,
      }).toDataUri();
    }

    // Default notionistsNeutral style
    const avatar = createAvatar(notionistsNeutral, {
      seed: finalSeed,
      size: finalSize,
      eyes: ["variant05"],
      brows: ["variant01", "variant12", "variant10"],
      lips: [
        "variant03",
        "variant04",
        "variant09",
        "variant10",
        "variant11",
        "variant14",
        "variant17",
        "variant22",
        "variant23",
        "variant25",
        "variant30",
      ],
      backgroundColor: AVATAR_COLORS,
      glasses: ["variant04", "variant11"],
      glassesProbability: 0.2,
      radius: 42,
    });

    return avatar.toDataUri();
  }, [finalSeed, finalSize, avatarStyle, backgroundColor, image]);

  return (
    <div
      className={`${
        image ? "relative rounded-[42%] overflow-hidden" : "relative"
      }`}
    >
      <img
        src={avatarSvg}
        alt={image ? "User provided avatar" : `Avatar for ${finalSeed}`}
        style={{
          display: "inline-block",
          objectFit: "cover",
          width: `${finalSize}px`,
          height: `${finalSize}px`,
          maxWidth: "none",
          minWidth: `${finalSize}px`,
          maxHeight: "none",
          flexShrink: 0,
          cursor: onClick ? "pointer" : "default",
          ...style,
        }}
        className={`${className}`}
        onClick={onClick}
      />
    </div>
  );
};

export default Avatar;
